import { z } from 'zod';

/**
 * Password Validation Schema
 *
 * Requirements:
 * - Minimum 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character (!@#$%^&*(),.?":{}|<>)
 */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character (!@#$%^&*(),.?":{}|<>)');

/**
 * Validates a password against security requirements
 * @param password - Password to validate
 * @returns Object with valid flag and error message
 */
export function validatePassword(password: string): { valid: boolean; error?: string } {
  try {
    passwordSchema.parse(password);
    return { valid: true };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { valid: false, error: error.errors[0].message };
    }
    return { valid: false, error: 'Invalid password' };
  }
}

/**
 * Calculate password strength (0-100)
 * @param password - Password to evaluate
 * @returns Strength score and level
 */
export function getPasswordStrength(password: string): {
  score: number;
  level: 'weak' | 'fair' | 'good' | 'strong' | 'very-strong';
  feedback: string[];
} {
  let score = 0;
  const feedback: string[] = [];

  // Length score (0-30 points)
  if (password.length >= 8) score += 10;
  if (password.length >= 12) score += 10;
  if (password.length >= 16) score += 10;
  else if (password.length < 8) {
    feedback.push('Use at least 8 characters');
  }

  // Character variety (0-40 points)
  if (/[a-z]/.test(password)) {
    score += 10;
  } else {
    feedback.push('Add lowercase letters');
  }

  if (/[A-Z]/.test(password)) {
    score += 10;
  } else {
    feedback.push('Add uppercase letters');
  }

  if (/[0-9]/.test(password)) {
    score += 10;
  } else {
    feedback.push('Add numbers');
  }

  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    score += 10;
  } else {
    feedback.push('Add special characters');
  }

  // Complexity bonus (0-30 points)
  // No repeated characters
  if (!/(.)\1{2,}/.test(password)) score += 10;
  else feedback.push('Avoid repeated characters');

  // No common patterns (123, abc, qwerty)
  if (!/(?:123|abc|qwerty|password)/i.test(password)) score += 10;
  else feedback.push('Avoid common patterns');

  // Mix of character types throughout
  if (password.length >= 8 && /(?=.*[a-z].*[a-z])(?=.*[A-Z].*[A-Z])(?=.*[0-9].*[0-9])/.test(password)) {
    score += 10;
  }

  // Determine level
  let level: 'weak' | 'fair' | 'good' | 'strong' | 'very-strong';
  if (score >= 80) level = 'very-strong';
  else if (score >= 60) level = 'strong';
  else if (score >= 40) level = 'good';
  else if (score >= 20) level = 'fair';
  else level = 'weak';

  return { score, level, feedback };
}

/**
 * Common passwords list (top 100 most common)
 * In production, this should be a larger list checked server-side
 */
const COMMON_PASSWORDS = [
  'password', '123456', '12345678', 'qwerty', 'abc123', 'monkey', '1234567',
  'letmein', 'trustno1', 'dragon', 'baseball', 'iloveyou', 'master', 'sunshine',
  'ashley', 'bailey', 'passw0rd', 'shadow', '123123', '654321', 'superman',
  'qazwsx', 'michael', 'football', 'password1', 'password123', 'admin'
];

/**
 * Check if password is in common passwords list
 * @param password - Password to check
 * @returns true if password is common
 */
export function isCommonPassword(password: string): boolean {
  return COMMON_PASSWORDS.includes(password.toLowerCase());
}
